'use client'

import { HospitalSearchSelector } from '@/components/HospitalSearchSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import {
    PayoutScheduleSection,
    PayoutTransferValue,
    PayoutScheduleValue,
    payoutScheduleToPayload,
    payoutTransferToPayload,
    validatePayoutScheduleClient,
} from '@/components/payout-schedule/PayoutScheduleSection'
import {
    DocumentType,
    ScholarLevel,
} from '@/lib/api/common/interfaces/professional.interface'
import { Role } from '@/lib/api/common/interfaces/role.interface'
import { Specialty } from '@/lib/api/common/interfaces/specialty.interface'
import {
    Establishment,
    getEstablishmentsByOrganization,
} from '@/lib/api/establishments/establishments-api'
import {
    fetchPostalCode,
    PostalCodeException,
} from '@/lib/api/fetch-postal-code'
import AddressManager, {
    EditableAddress,
} from '@/components/address/address-manager'
import { getAddresses, syncAddresses, toEditable } from '@/lib/api/addresses'
import {
    createProfessional,
    getProfessional,
    updateProfessional,
    uploadProfessionalPhoto,
} from '@/lib/api/professionals/professionals-api'
import { getDefaultRole, getRoles } from '@/lib/api/roles/roles-api'
import {
    getAllWithSubspecialtiesByOrganization,
    getSpecialtiesByOrganization,
} from '@/lib/api/specialties/specialties-api'
import { BRAZILIAN_COUNCILS } from '@/lib/constants/brazilian-councils'
import { filterCommonSpecialties } from '@/lib/constants/common-specialties'
import { formatPhoneNumber, mask, unMask } from '@/lib/form-state/util'
import { useAuth } from '@/store/hooks'
import { useImageCrop } from '@/components/image-crop'
import { useEffect, useState } from 'react'
import { MdArrowBack, MdClose, MdPhotoCamera, MdSave, MdWarning } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useServiceLabel } from '@/lib/hooks/use-service-label'

const formatDocument = (value: string, type: string): string => {
    const digits = value.replace(/\D/g, '')
    if (type === 'CNPJ') {
        return digits
            .slice(0, 14)
            .replace(/(\d{2})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1/$2')
            .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
    }
    return digits
        .slice(0, 11)
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

interface SelectedSpecialty {
    id?: number
    name: string
    isNew?: boolean
    consultationPrice?: string
    maxDiscount?: string
}

interface FormData {
    name: string
    email: string
    phoneNumber: string
    whatsAppNumber: string
    phone2: string
    phone3: string
    formation: string
    scholarLevel: string
    document: string
    documentType: string
    registrationNumber: string
    councilName: string
    street: string
    number: string
    complement: string
    neighborhood: string
    city: string
    state: string
    zipCode: string
    country: string
    specialtyIds: number[]
    specialtyNames: string[]
    roleId: string
    hospitalIds: number[]
    partnerId: number | null
    createUser: boolean
    titles: string[]
    qualifications: string[]
    establishmentIds: number[]
    photoUrl?: string
}

const currencyFormat = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) value = '0'
    value = `${value}`.replace(/[^0-9]+/g, '')
    return mask(value, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

const parseCurrencyToCents = (value: string): number => {
    if (!value.trim()) return 0
    const unmaskedValue = unMask(value, 'currency')
    return parseInt(unmaskedValue)
}

const formatCentsToDisplay = (cents: number | null | undefined): string => {
    if (cents === null || cents === undefined || cents === 0) return ''
    return currencyFormat(cents.toString())
}

export default function ProfessionalFormPage() {
    const { isHealth } = useServiceLabel()
    const { user, isPartner, isEmployee } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id
    const hidePartnerSelector = isPartner || (isEmployee && !!user.partnerId)

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [loadingCep, setLoadingCep] = useState(false)
    const [specialties, setSpecialties] = useState<Specialty[]>([])
    const [loadingSpecialties, setLoadingSpecialties] = useState(false)
    const [selectedSpecialties, setSelectedSpecialties] = useState<
        SelectedSpecialty[]
    >([])
    const [specialtyInput, setSpecialtyInput] = useState('')
    const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false)
    const [showAddHint, setShowAddHint] = useState(false)
    const [loadingRoles, setLoadingRoles] = useState(false)
    // A role padrão de profissionais é a SUGESTÃO inicial — o operador pode trocar por
    // outro perfil da organização. Vem do endpoint dedicado (mesma consulta do backend),
    // NÃO da lista geral `getRoles` — aquela é filtrada por isSystem/partnerId e pode
    // esconder a role padrão.
    const [defaultProfessionalRole, setDefaultProfessionalRole] =
        useState<Role | null>(null)
    /** Perfis da organização oferecidos no seletor de acesso. */
    const [roles, setRoles] = useState<Role[]>([])
    const hasDefaultProfessionalRole = !!defaultProfessionalRole
    const defaultProfessionalRoleName = defaultProfessionalRole?.name ?? ''
    /** Locais onde o profissional atende. Persistidos no submit. */
    const [addresses, setAddresses] = useState<EditableAddress[]>([])
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        phoneNumber: '',
        whatsAppNumber: '',
        phone2: '',
        phone3: '',
        formation: '',
        scholarLevel: '',
        document: '',
        documentType: '',
        registrationNumber: '',
        councilName: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'BR',
        specialtyIds: [],
        specialtyNames: [],
        roleId: '',
        hospitalIds: [],
        partnerId: hidePartnerSelector ? user.partnerId || null : null,
        // Acesso ao sistema é OPT-IN: só liga quando o operador clica em "Permitir
        // acesso ao sistema". Sem isso, não pede perfil nem dispara e-mail de senha.
        createUser: false,
        titles: [],
        qualifications: [],
        establishmentIds: [],
    })

    const [payoutSchedule, setPayoutSchedule] = useState<PayoutScheduleValue>({
        payoutScheduleType: 'OPEN',
        payoutFixedDay: '',
        payoutDaysAfterAppointment: '',
    })
    /** Dados para transferência do repasse (PIX ou banco). */
    const [payoutTransfer, setPayoutTransfer] = useState<PayoutTransferValue>({
        pixKey: '',
        pixKeyType: '',
        bankCode: '',
        bankAgency: '',
        bankAccount: '',
        bankAccountDigit: '',
        bankAccountType: 'CONTA_CORRENTE',
    })

    // Estado do acesso no momento em que a tela abriu: só assim dá para avisar
    // que o e-mail de criação de senha saiu (acesso concedido agora, nesta edição).
    const [hadSystemAccess, setHadSystemAccess] = useState(false)
    // Já existia um usuário vinculado (mesmo desativado)? Nesse caso conceder o
    // acesso apenas reativa a conta — não sai e-mail de criação de senha.
    const [hadLinkedUser, setHadLinkedUser] = useState(false)
    const [photoFile, setPhotoFile] = useState<File | null>(null)
    // A foto é exibida redonda (painel e loja): corte quadrado com prévia circular.
    const photoCrop = useImageCrop({
        title: 'Ajustar foto',
        aspect: 1,
        round: true,
        output: { maxWidth: 512 },
    })
    const [photoPreview, setPhotoPreview] = useState<string | null>(null)
    const [titleInput, setTitleInput] = useState('')
    const [qualificationInput, setQualificationInput] = useState('')
    const [establishments, setEstablishments] = useState<Establishment[]>([])
    const [establishmentSearch, setEstablishmentSearch] = useState('')
    const [showEstablishmentDropdown, setShowEstablishmentDropdown] =
        useState(false)
    const [customCouncil, setCustomCouncil] = useState('')
    const [subspecialtyInput, setSubspecialtyInput] = useState('')
    const [showSubspecialtyDropdown, setShowSubspecialtyDropdown] =
        useState(false)
    const [selectedSubspecialties, setSelectedSubspecialties] = useState<
        SelectedSpecialty[]
    >([])
    const [allSpecialtiesWithSubs, setAllSpecialtiesWithSubs] = useState<
        Specialty[]
    >([])

    useEffect(() => {
        if (user?.companyId) {
            loadSpecialties()
            loadDefaultProfessionalRole()
            loadRoles()

            // Load establishments
            getEstablishmentsByOrganization(user.organizationId, {
                activeOnly: true,
            }).then(res => {
                if (res.success)
                    setEstablishments(
                        Array.isArray(res.result) ? res.result : []
                    )
            })

            // Load all specialties with subspecialties for the subspecialty autocomplete
            getAllWithSubspecialtiesByOrganization(user.organizationId).then(
                res => {
                    if (res.success) {
                        setAllSpecialtiesWithSubs(res.result)
                    }
                }
            )
        }
    }, [user?.companyId])

    useEffect(() => {
        if (isEditing && id) {
            loadProfessional()
        }
    }, [id, isEditing])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement
            if (
                !target.closest('.specialty-autocomplete-container') &&
                showSpecialtyDropdown
            ) {
                setShowSpecialtyDropdown(false)
            }
            if (
                !target.closest('.subspecialty-autocomplete-container') &&
                showSubspecialtyDropdown
            ) {
                setShowSubspecialtyDropdown(false)
            }
            if (
                !target.closest('.establishment-autocomplete-container') &&
                showEstablishmentDropdown
            ) {
                setShowEstablishmentDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [
        showSpecialtyDropdown,
        showSubspecialtyDropdown,
        showEstablishmentDropdown,
    ])

    const loadSpecialties = async () => {
        if (!user?.companyId) return

        setLoadingSpecialties(true)
        try {
            const response = await getSpecialtiesByOrganization(
                user.organizationId
            )
            if (response.success) {
                setSpecialties(response.result || [])
            }
        } catch (error) {
            console.error('Erro ao carregar especialidades:', error)
        } finally {
            setLoadingSpecialties(false)
        }
    }

    const loadRoles = async () => {
        try {
            const response = await getRoles()
            if (response.success && response.result) {
                setRoles(
                    Array.isArray(response.result)
                        ? response.result
                        : [response.result]
                )
            }
        } catch (error) {
            console.error('Erro ao carregar perfis de acesso:', error)
        }
    }

    // Carrega a role padrão de profissionais direto do endpoint dedicado (fonte de
    // verdade que o backend usa quando nenhum perfil é escolhido). A lista geral de
    // roles alimenta o seletor, mas pode esconder a padrão — por isso as duas.
    const loadDefaultProfessionalRole = async () => {
        setLoadingRoles(true)
        try {
            const response = await getDefaultRole('PROFESSIONAL')
            if (response.success) {
                setDefaultProfessionalRole(response.result ?? null)
            }
        } catch (error) {
            console.error('Erro ao carregar perfil padrão de profissionais:', error)
        } finally {
            setLoadingRoles(false)
        }
    }

    const loadProfessional = async () => {
        if (!id) return

        setLoadingData(true)
        try {
            const response = await getProfessional(parseInt(id, 10))
            if (response.success && response.result) {
                const professional = response.result
                const parentSpecialties =
                    professional.specialties?.filter(
                        s => !s.specialty.parentId
                    ) || []
                const subSpecialties =
                    professional.specialties?.filter(
                        s => !!s.specialty.parentId
                    ) || []

                const hasAccess = professional.user
                    ? professional.user.active !== false
                    : !!professional.userId
                setHadSystemAccess(hasAccess)
                setHadLinkedUser(!!(professional.user || professional.userId))

                setFormData({
                    name: professional.name || '',
                    email: professional.email || '',
                    phoneNumber: formatPhoneNumber(professional.phoneNumber),
                    whatsAppNumber: formatPhoneNumber(
                        professional.whatsAppNumber
                    ),
                    phone2: formatPhoneNumber(professional.phone2),
                    phone3: formatPhoneNumber(professional.phone3),
                    formation: professional.formation || '',
                    scholarLevel: professional.scholarLevel || '',
                    document: professional.document
                        ? formatDocument(
                              professional.document,
                              professional.documentType || 'CPF'
                          )
                        : '',
                    documentType: professional.documentType || '',
                    registrationNumber: professional.registrationNumber || '',
                    councilName: professional.councilName || '',
                    street: professional.street || '',
                    number: professional.number || '',
                    complement: professional.complement || '',
                    neighborhood: professional.neighborhood || '',
                    city: professional.city || '',
                    state: professional.state || '',
                    zipCode: professional.zipCode || '',
                    country: professional.country || 'BR',
                    specialtyIds:
                        professional.specialties?.map(s => s.specialty.id) ||
                        [],
                    specialtyNames: [],
                    roleId: professional.user?.roleId
                        ? String(professional.user.roleId)
                        : '',
                    hospitalIds:
                        professional.hospitals?.map(h => h.hospital.id) || [],
                    partnerId: professional.partnerId || null,
                    // "Tem acesso ao sistema" = possui User vinculado E ativo.
                    // Revogar o acesso desativa o usuário sem desfazer o vínculo,
                    // então o usuário inativo aparece aqui como sem acesso.
                    createUser: hasAccess,
                    titles: professional.titles?.map((t: any) => t.title) || [],
                    qualifications:
                        professional.qualifications?.map(
                            (q: any) => q.description
                        ) || [],
                    establishmentIds:
                        professional.establishments?.map(
                            (e: any) => e.establishment.id
                        ) || [],
                    photoUrl: professional.photoUrl || undefined,
                })

                if (professional.photoUrl) {
                    setPhotoPreview(professional.photoUrl)
                }

                setPayoutSchedule({
                    payoutScheduleType:
                        professional.payoutScheduleType || 'OPEN',
                    payoutFixedDay: professional.payoutFixedDay
                        ? String(professional.payoutFixedDay)
                        : '',
                    payoutDaysAfterAppointment:
                        professional.payoutDaysAfterAppointment
                            ? String(professional.payoutDaysAfterAppointment)
                            : '',
                })
                setPayoutTransfer({
                    pixKey: (professional as any).pixKey || '',
                    pixKeyType: (professional as any).pixKeyType || '',
                    bankCode: (professional as any).bankCode || '',
                    bankAgency: (professional as any).bankAgency || '',
                    bankAccount: (professional as any).bankAccount || '',
                    bankAccountDigit: (professional as any).bankAccountDigit || '',
                    bankAccountType:
                        (professional as any).bankAccountType || 'CONTA_CORRENTE',
                })

                setSelectedSpecialties(
                    parentSpecialties.map(s => ({
                        id: s.specialty.id,
                        name: s.specialty.name,
                        isNew: false,
                        consultationPrice: s.consultationPrice
                            ? formatCentsToDisplay(s.consultationPrice)
                            : '',
                        maxDiscount: s.maxDiscount?.toString() || '',
                    }))
                )

                setSelectedSubspecialties(
                    subSpecialties.map(s => ({
                        id: s.specialty.id,
                        name: s.specialty.name,
                        isNew: false,
                    }))
                )

                const addressResponse = await getAddresses({
                    professionalId: professional.id,
                })
                if (addressResponse.success) {
                    setAddresses(toEditable(addressResponse.result))
                }
            } else {
                toast.error(response.error || 'Erro ao carregar profissional')
                navigate('/person/professionals')
            }
        } catch (error) {
            toast.error('Erro ao carregar profissional')
            navigate('/person/professionals')
        } finally {
            setLoadingData(false)
        }
    }

    const formatZipCode = (value: string): string => {
        const numbers = value.replace(/\D/g, '')
        if (numbers.length <= 5) {
            return numbers
        }
        return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`
    }

    const handleZipCodeChange = async (value: string) => {
        const formatted = formatZipCode(value)
        updateFormField('zipCode', formatted)

        const cleanCode = formatted.replace(/\D/g, '')
        if (cleanCode.length === 8) {
            setLoadingCep(true)
            try {
                const postalData = await fetchPostalCode(cleanCode)
                setFormData(prev => ({
                    ...prev,
                    zipCode: formatted,
                    street: postalData.logradouro || prev.street,
                    neighborhood: postalData.bairro || prev.neighborhood,
                    city: postalData.localidade || prev.city,
                    state: postalData.uf || prev.state,
                    complement: postalData.complemento || prev.complement,
                }))
            } catch (error) {
                if (error instanceof PostalCodeException) {
                    toast.error(error.message || 'CEP não encontrado')
                } else {
                    toast.error('Erro ao buscar CEP')
                }
            } finally {
                setLoadingCep(false)
            }
        }
    }

    const updateFormField = (
        field: keyof FormData,
        value: string | number[]
    ) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const handleChange = (field: keyof FormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const checkSpecialtyExists = (name: string): Specialty | null => {
        const normalizedName = name.trim().toLowerCase()
        return (
            specialties.find(s => s.name.toLowerCase() === normalizedName) ||
            null
        )
    }

    const handleAddSpecialty = () => {
        const trimmedName = specialtyInput.trim()
        if (!trimmedName) {
            toast.error('Digite o nome da especialidade')
            return
        }

        // Verificar se já está selecionada
        const alreadySelected = selectedSpecialties.some(
            s => s.name.toLowerCase() === trimmedName.toLowerCase()
        )
        if (alreadySelected) {
            toast.error('Esta especialidade já foi adicionada')
            setSpecialtyInput('')
            setShowSpecialtyDropdown(false)
            setShowAddHint(false)
            return
        }

        // Verificar se já existe no banco
        const existingSpecialty = checkSpecialtyExists(trimmedName)
        if (existingSpecialty) {
            // Adicionar como existente (ID)
            const newSpecialty: SelectedSpecialty = {
                id: existingSpecialty.id,
                name: existingSpecialty.name,
                isNew: false,
            }
            setSelectedSpecialties(prev => [...prev, newSpecialty])
            setFormData(prev => ({
                ...prev,
                specialtyIds: [...prev.specialtyIds, existingSpecialty.id],
            }))
        } else {
            // Adicionar como nova (nome)
            const newSpecialty: SelectedSpecialty = {
                name: trimmedName,
                isNew: true,
            }
            setSelectedSpecialties(prev => [...prev, newSpecialty])
            setFormData(prev => ({
                ...prev,
                specialtyNames: [...prev.specialtyNames, trimmedName],
            }))
        }

        setSpecialtyInput('')
        setShowSpecialtyDropdown(false)
        setShowAddHint(false)
    }

    const handleRemoveSpecialty = (index: number) => {
        const specialty = selectedSpecialties[index]
        setSelectedSpecialties(prev => prev.filter((_, i) => i !== index))

        if (specialty.id) {
            // Remover ID
            setFormData(prev => ({
                ...prev,
                specialtyIds: prev.specialtyIds.filter(
                    id => id !== specialty.id
                ),
            }))
        } else {
            // Remover nome
            setFormData(prev => ({
                ...prev,
                specialtyNames: prev.specialtyNames.filter(
                    name => name !== specialty.name
                ),
            }))
        }
    }

    // Filtrar especialidades cadastradas no servidor
    const filteredServerSpecialties = specialties.filter(
        s =>
            !selectedSpecialties.some(
                selected =>
                    selected.id === s.id ||
                    selected.name.toLowerCase() === s.name.toLowerCase()
            ) &&
            s.name.toLowerCase().includes(specialtyInput.toLowerCase().trim())
    )

    // Obter sugestoes da lista de especialidades comuns
    const selectedNames = selectedSpecialties.map(s => s.name)
    const serverNames = filteredServerSpecialties.map(s => s.name.toLowerCase())
    const commonSuggestions = filterCommonSpecialties(
        specialtyInput,
        [...selectedNames, ...serverNames],
        10
    )

    // Combinar resultados: primeiro do servidor, depois sugestoes
    const hasSuggestions =
        filteredServerSpecialties.length > 0 || commonSuggestions.length > 0

    const validateForm = (): boolean => {
        if (!formData.name.trim()) {
            toast.error('Nome é obrigatório')
            return false
        }
        // O perfil pode ser escolhido; sem escolha, cai no perfil padrão de
        // profissionais. Sem nenhum dos dois, não há como conceder acesso.
        if (
            formData.createUser &&
            !formData.roleId &&
            !hasDefaultProfessionalRole
        ) {
            toast.error(
                'Selecione um perfil de acesso ou defina um perfil padrão para profissionais.'
            )
            return false
        }
        if (formData.createUser && !formData.email.trim()) {
            toast.error(
                'Email é obrigatório quando o profissional terá acesso ao sistema'
            )
            return false
        }
        const payoutError = validatePayoutScheduleClient(payoutSchedule)
        if (payoutError) {
            toast.warning(payoutError)
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) return
        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        setLoading(true)
        try {
            if (isEditing && id) {
                const councilValue =
                    formData.councilName === 'OUTRO'
                        ? customCouncil.trim()
                        : formData.councilName.trim()
                const response = await updateProfessional(parseInt(id, 10), {
                    name: formData.name.trim() || undefined,
                    email: formData.email.trim() || undefined,
                    phoneNumber: unMask(formData.phoneNumber) || undefined,
                    whatsAppNumber:
                        unMask(formData.whatsAppNumber) || undefined,
                    phone2: unMask(formData.phone2) || undefined,
                    phone3: unMask(formData.phone3) || undefined,
                    formation: formData.formation.trim() || undefined,
                    scholarLevel: formData.scholarLevel || undefined,
                    document: unMask(formData.document) || undefined,
                    documentType: formData.documentType || undefined,
                    registrationNumber:
                        formData.registrationNumber.trim() || undefined,
                    councilName: councilValue || undefined,
                    // Endereço saiu do payload: virou tabela própria, gravada
                    // por `syncAddresses` após o salvamento. Ver model `Address`.
                    specialtyIds: formData.specialtyIds,
                    specialtyNames: formData.specialtyNames,
                    specialties: selectedSpecialties.map(s => ({
                        specialtyId: s.id,
                        specialtyName: s.isNew ? s.name : undefined,
                        consultationPrice: s.consultationPrice
                            ? parseCurrencyToCents(s.consultationPrice)
                            : undefined,
                        maxDiscount: s.maxDiscount
                            ? parseFloat(s.maxDiscount)
                            : undefined,
                    })),
                    // Perfil escolhido pelo operador; sem escolha, o backend aplica
                    // o perfil padrão de profissionais da organização.
                    roleId: formData.createUser
                        ? formData.roleId
                            ? parseInt(formData.roleId, 10)
                            : defaultProfessionalRole?.id
                        : undefined,
                    createUser: formData.createUser,
                    titles:
                        formData.titles.length > 0
                            ? formData.titles
                            : undefined,
                    qualifications:
                        formData.qualifications.length > 0
                            ? formData.qualifications
                            : undefined,
                    establishmentIds: formData.establishmentIds,
                    hospitalIds: formData.hospitalIds,
                    partnerId: formData.partnerId,
                    ...payoutScheduleToPayload(payoutSchedule),
                    ...payoutTransferToPayload(payoutTransfer),
                })
                if (response.success) {
                    if (photoFile) {
                        try {
                            await uploadProfessionalPhoto(
                                parseInt(id, 10),
                                photoFile
                            )
                        } catch {
                            toast.warning(
                                'Profissional salvo, mas erro ao enviar foto'
                            )
                        }
                    }
                    try {
                        await syncAddresses(
                            { professionalId: parseInt(id, 10) },
                            addresses,
                        )
                    } catch {
                        toast.error(
                            'Profissional salvo, mas houve erro ao salvar os endereços'
                        )
                    }
                    toast.success('Profissional atualizado com sucesso!')
                    if (formData.createUser && !hadLinkedUser) {
                        toast.info(
                            `Um email foi enviado para ${formData.email.trim()} com instruções para criar a senha de acesso ao sistema.`,
                            { autoClose: 8000 }
                        )
                    } else if (formData.createUser && !hadSystemAccess) {
                        toast.info('Acesso ao sistema reativado.')
                    } else if (!formData.createUser && hadSystemAccess) {
                        toast.info('Acesso ao sistema revogado.')
                    }
                    navigate(`/person/professionals/${id}`)
                } else {
                    toast.error(
                        response.error || 'Erro ao atualizar profissional'
                    )
                }
            } else {
                const professionalEmail = formData.email.trim()
                const createCouncilValue =
                    formData.councilName === 'OUTRO'
                        ? customCouncil.trim()
                        : formData.councilName.trim()
                const response = await createProfessional({
                    name: formData.name.trim(),
                    email: professionalEmail || undefined,
                    phoneNumber: unMask(formData.phoneNumber) || undefined,
                    whatsAppNumber:
                        unMask(formData.whatsAppNumber) || undefined,
                    phone2: unMask(formData.phone2) || undefined,
                    phone3: unMask(formData.phone3) || undefined,
                    formation: formData.formation.trim() || undefined,
                    scholarLevel: formData.scholarLevel || undefined,
                    document: unMask(formData.document) || undefined,
                    documentType: formData.documentType || undefined,
                    registrationNumber:
                        formData.registrationNumber.trim() || undefined,
                    councilName: createCouncilValue || undefined,
                    // Endereço saiu do payload: virou tabela própria, gravada
                    // por `syncAddresses` após o salvamento. Ver model `Address`.
                    organizationId: user.organizationId,
                    specialtyIds: formData.specialtyIds,
                    specialtyNames: formData.specialtyNames,
                    specialties: selectedSpecialties.map(s => ({
                        specialtyId: s.id,
                        specialtyName: s.isNew ? s.name : undefined,
                        consultationPrice: s.consultationPrice
                            ? parseCurrencyToCents(s.consultationPrice)
                            : undefined,
                        maxDiscount: s.maxDiscount
                            ? parseFloat(s.maxDiscount)
                            : 0,
                    })),
                    // Perfil escolhido pelo operador; sem escolha, o backend aplica
                    // o perfil padrão de profissionais da organização.
                    roleId: formData.createUser
                        ? formData.roleId
                            ? parseInt(formData.roleId, 10)
                            : defaultProfessionalRole?.id
                        : undefined,
                    createUser: formData.createUser,
                    titles:
                        formData.titles.length > 0
                            ? formData.titles
                            : undefined,
                    qualifications:
                        formData.qualifications.length > 0
                            ? formData.qualifications
                            : undefined,
                    establishmentIds: formData.establishmentIds,
                    hospitalIds: formData.hospitalIds,
                    partnerId: formData.partnerId,
                    ...payoutScheduleToPayload(payoutSchedule),
                    ...payoutTransferToPayload(payoutTransfer),
                })
                if (response.success && response.result) {
                    if (photoFile && response.result.id) {
                        try {
                            await uploadProfessionalPhoto(
                                response.result.id,
                                photoFile
                            )
                        } catch {
                            toast.warning(
                                'Profissional salvo, mas erro ao enviar foto'
                            )
                        }
                    }
                    if (response.result.id) {
                        try {
                            await syncAddresses(
                                { professionalId: response.result.id },
                                addresses,
                            )
                        } catch {
                            toast.error(
                                'Profissional salvo, mas houve erro ao salvar os endereços'
                            )
                        }
                    }
                    toast.success('Profissional criado com sucesso!')
                    if (professionalEmail) {
                        toast.info(
                            `Um email foi enviado para ${professionalEmail} com instruções para criar a senha de acesso ao sistema.`,
                            {
                                autoClose: 8000,
                            }
                        )
                    }
                    navigate(`/person/professionals/${response.result.id}`)
                } else {
                    toast.error(response.error || 'Erro ao criar profissional')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar profissional')
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando dados do profissional...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/person/professionals')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-base-content">
                        {isEditing
                            ? 'Editar Profissional'
                            : 'Novo Profissional'}
                    </h1>
                    <p className="text-sm text-base-content/60">
                        {isEditing
                            ? 'Atualize as informações do profissional'
                            : 'Preencha os dados do novo profissional'}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Foto */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Foto</h2>
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                {photoPreview || formData.photoUrl ? (
                                    <img
                                        src={photoPreview || ''}
                                        alt="Foto"
                                        className="w-24 h-24 rounded-full object-cover border border-base-300/50"
                                    />
                                ) : (
                                    <div className="w-24 h-24 rounded-full bg-base-200 flex items-center justify-center">
                                        <MdPhotoCamera className="text-3xl text-base-content/30" />
                                    </div>
                                )}
                                {photoPreview && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setPhotoPreview(null)
                                            setPhotoFile(null)
                                        }}
                                        className="absolute -top-2 -right-2 btn btn-circle btn-xs btn-error"
                                    >
                                        <MdClose className="text-xs" />
                                    </button>
                                )}
                            </div>
                            <div>
                                <label className="btn btn-outline btn-sm gap-2 cursor-pointer">
                                    <MdPhotoCamera className="text-lg" />
                                    {photoPreview ? 'Alterar' : 'Enviar foto'}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={async e => {
                                            const file = e.target.files?.[0]
                                            e.target.value = ''
                                            if (!file) return
                                            if (
                                                !file.type.startsWith('image/')
                                            ) {
                                                toast.error(
                                                    'Apenas imagens são permitidas'
                                                )
                                                return
                                            }
                                            if (file.size > 25 * 1024 * 1024) {
                                                toast.error(
                                                    'Tamanho máximo: 25MB'
                                                )
                                                return
                                            }
                                            const cropped =
                                                await photoCrop.cropFile(file)
                                            if (!cropped) return
                                            setPhotoFile(cropped)
                                            setPhotoPreview(
                                                URL.createObjectURL(cropped)
                                            )
                                        }}
                                    />
                                </label>
                                <p className="text-xs text-base-content/50 mt-1">
                                    PNG, JPG ou WEBP. A foto é cortada e
                                    comprimida ao escolher.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Informações Básicas
                        </h2>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Nome completo do profissional"
                                value={formData.name}
                                onChange={e =>
                                    updateFormField('name', e.target.value)
                                }
                                required
                            />
                        </div>

                        <div className="form-control md:col-span-2 mt-4">
                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary"
                                    checked={formData.createUser}
                                    onChange={e =>
                                        handleChange(
                                            'createUser',
                                            e.target.checked
                                        )
                                    }
                                />
                                <span className="label-text font-medium">
                                    Permitir acesso ao sistema
                                </span>
                            </label>
                            <span className="text-xs text-base-content/60 mt-1 ml-1">
                                {formData.createUser
                                    ? 'O profissional receberá um e-mail para criar a senha e poderá fazer login.'
                                    : 'Ative para o profissional fazer login. Sem isso, ele é apenas um cadastro (sem perfil de acesso nem e-mail de senha).'}
                            </span>

                            {formData.createUser &&
                                !loadingRoles &&
                                !hasDefaultProfessionalRole && (
                                    <div className="alert alert-warning mt-3 py-2 px-3 text-sm">
                                        <MdWarning className="text-lg shrink-0" />
                                        <span>
                                            Para este profissional fazer login
                                            no sistema, defina um perfil padrão
                                            para profissionais em Perfis de
                                            Acesso.
                                        </span>
                                    </div>
                                )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Email{' '}
                                        {formData.createUser && (
                                            <span className="text-error">
                                                *
                                            </span>
                                        )}
                                    </span>
                                </label>
                                <input
                                    type="email"
                                    className="input input-bordered w-full focus:input-primary"
                                    placeholder="email@exemplo.com"
                                    value={formData.email}
                                    onChange={e =>
                                        updateFormField('email', e.target.value)
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Telefone
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full focus:input-primary"
                                    placeholder="(11) 99999-9999"
                                    value={formData.phoneNumber}
                                    onChange={e =>
                                        updateFormField(
                                            'phoneNumber',
                                            formatPhoneNumber(e.target.value)
                                        )
                                    }
                                    maxLength={15}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Telefone 2
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full focus:input-primary"
                                    placeholder="(00) 00000-0000"
                                    value={formData.phone2}
                                    onChange={e =>
                                        handleChange(
                                            'phone2',
                                            formatPhoneNumber(e.target.value)
                                        )
                                    }
                                    maxLength={15}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Telefone 3
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full focus:input-primary"
                                    placeholder="(00) 00000-0000"
                                    value={formData.phone3}
                                    onChange={e =>
                                        handleChange(
                                            'phone3',
                                            formatPhoneNumber(e.target.value)
                                        )
                                    }
                                    maxLength={15}
                                />
                            </div>
                        </div>

                        {/*<div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    WhatsApp
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full max-w-md focus:input-primary"
                                placeholder="5511999999999"
                                value={formData.whatsAppNumber}
                                onChange={e =>
                                    updateFormField(
                                        'whatsAppNumber',
                                        e.target.value
                                    )
                                }
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/60">
                                    Número utilizado para ativar o WhatsApp no módulo de atendimento
                                </span>
                            </label>
                        </div>*/}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Formação
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full focus:input-primary"
                                    placeholder="Ex: Medicina, Enfermagem"
                                    value={formData.formation}
                                    onChange={e =>
                                        updateFormField(
                                            'formation',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Escolaridade
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full focus:select-primary"
                                    value={formData.scholarLevel}
                                    onChange={e =>
                                        updateFormField(
                                            'scholarLevel',
                                            e.target.value
                                        )
                                    }
                                >
                                    <option value="">Selecione...</option>
                                    {Object.values(ScholarLevel).map(level => (
                                        <option key={level} value={level}>
                                            {level === ScholarLevel.MEDIO
                                                ? 'Ensino Médio'
                                                : level === ScholarLevel.TECNICO
                                                  ? 'Ensino Técnico'
                                                  : level ===
                                                      ScholarLevel.POS_GRADUACAO
                                                    ? 'Pós-Graduação'
                                                    : level.replace('_', ' ')}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/*
                            O perfil padrão de profissionais vem pré-selecionado, mas
                            pode ser trocado por outro perfil da organização. O backend
                            recusa perfil de outra organização ou de sistema.
                        */}
                        {formData.createUser && (
                            <div className="form-control w-full mt-4">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Perfil de Acesso
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full focus:select-primary"
                                    value={formData.roleId}
                                    onChange={e =>
                                        updateFormField(
                                            'roleId',
                                            e.target.value
                                        )
                                    }
                                    disabled={loadingRoles}
                                >
                                    <option value="">
                                        {loadingRoles
                                            ? 'Carregando...'
                                            : hasDefaultProfessionalRole
                                              ? `Perfil padrão (${defaultProfessionalRoleName})`
                                              : 'Selecione um perfil'}
                                    </option>
                                    {roles.map(role => (
                                        <option
                                            key={role.id}
                                            value={String(role.id)}
                                        >
                                            {role.name}
                                            {role.id ===
                                            defaultProfessionalRole?.id
                                                ? ' (Padrão)'
                                                : ''}
                                        </option>
                                    ))}
                                </select>
                                <span className="text-xs text-base-content/60 mt-1">
                                    O perfil define o que o profissional enxerga
                                    no sistema. Sem escolher, é aplicado o
                                    perfil padrão de profissionais.
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Documentação
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Tipo de Documento
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full focus:select-primary"
                                    value={formData.documentType}
                                    onChange={e => {
                                        const newType = e.target.value
                                        updateFormField('documentType', newType)
                                        if (formData.document) {
                                            updateFormField(
                                                'document',
                                                formatDocument(
                                                    formData.document,
                                                    newType || 'CPF'
                                                )
                                            )
                                        }
                                    }}
                                >
                                    <option value="">Selecione...</option>
                                    {Object.values(DocumentType).map(type => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Documento
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full focus:input-primary"
                                    placeholder={
                                        formData.documentType === 'CNPJ'
                                            ? '00.000.000/0000-00'
                                            : '000.000.000-00'
                                    }
                                    value={formData.document}
                                    onChange={e =>
                                        updateFormField(
                                            'document',
                                            formatDocument(
                                                e.target.value,
                                                formData.documentType || 'CPF'
                                            )
                                        )
                                    }
                                    maxLength={
                                        formData.documentType === 'CNPJ'
                                            ? 18
                                            : 14
                                    }
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Número de Registro
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full focus:input-primary"
                                    placeholder="Ex: CRM, Coren, etc."
                                    value={formData.registrationNumber}
                                    onChange={e =>
                                        updateFormField(
                                            'registrationNumber',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Conselho
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full focus:select-primary"
                                    value={formData.councilName}
                                    onChange={e =>
                                        handleChange(
                                            'councilName',
                                            e.target.value
                                        )
                                    }
                                >
                                    <option value="">
                                        Selecione o conselho
                                    </option>
                                    {BRAZILIAN_COUNCILS.map(c => (
                                        <option key={c.value} value={c.value}>
                                            {c.label}
                                        </option>
                                    ))}
                                </select>
                                {formData.councilName === 'OUTRO' && (
                                    <div className="form-control mt-2">
                                        <input
                                            type="text"
                                            className="input input-bordered w-full focus:input-primary"
                                            placeholder="Nome do conselho"
                                            value={customCouncil}
                                            onChange={e =>
                                                setCustomCouncil(e.target.value)
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Títulos */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Títulos</h2>
                        <div className="flex gap-2 mb-3">
                            <input
                                type="text"
                                className="input input-bordered flex-1"
                                placeholder="Ex: Membro Titular da Sociedade Brasileira..."
                                value={titleInput}
                                onChange={e => setTitleInput(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        if (titleInput.trim()) {
                                            handleChange('titles', [
                                                ...formData.titles,
                                                titleInput.trim(),
                                            ])
                                            setTitleInput('')
                                        }
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="btn btn-secondary"
                                disabled={!titleInput.trim()}
                                onClick={() => {
                                    handleChange('titles', [
                                        ...formData.titles,
                                        titleInput.trim(),
                                    ])
                                    setTitleInput('')
                                }}
                            >
                                Adicionar
                            </button>
                        </div>
                        {formData.titles.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {formData.titles.map((t, i) => (
                                    <span
                                        key={i}
                                        className="badge badge-secondary gap-1 py-3"
                                    >
                                        {t}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleChange(
                                                    'titles',
                                                    formData.titles.filter(
                                                        (_, idx) => idx !== i
                                                    )
                                                )
                                            }
                                        >
                                            <MdClose className="text-sm" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Especializações */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Especializações
                        </h2>
                        <div className="flex gap-2 mb-3">
                            <input
                                type="text"
                                className="input input-bordered flex-1"
                                placeholder="Ex: Residência Médica..."
                                value={qualificationInput}
                                onChange={e =>
                                    setQualificationInput(e.target.value)
                                }
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        if (qualificationInput.trim()) {
                                            handleChange('qualifications', [
                                                ...formData.qualifications,
                                                qualificationInput.trim(),
                                            ])
                                            setQualificationInput('')
                                        }
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="btn btn-secondary"
                                disabled={!qualificationInput.trim()}
                                onClick={() => {
                                    handleChange('qualifications', [
                                        ...formData.qualifications,
                                        qualificationInput.trim(),
                                    ])
                                    setQualificationInput('')
                                }}
                            >
                                Adicionar
                            </button>
                        </div>
                        {formData.qualifications.length > 0 && (
                            <div className="flex flex-col gap-2">
                                {formData.qualifications.map((q, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center justify-between p-2 bg-base-200/50 rounded-lg"
                                    >
                                        <span className="text-sm">{q}</span>
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-xs text-error"
                                            onClick={() =>
                                                handleChange(
                                                    'qualifications',
                                                    formData.qualifications.filter(
                                                        (_, idx) => idx !== i
                                                    )
                                                )
                                            }
                                        >
                                            <MdClose className="text-sm" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Especialidades
                        </h2>

                        <div className="space-y-4">
                            <div className="relative specialty-autocomplete-container">
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        className="input input-bordered w-full focus:input-primary"
                                        placeholder="Digite ou selecione uma especialidade"
                                        value={specialtyInput}
                                        onChange={e => {
                                            setSpecialtyInput(e.target.value)
                                            setShowSpecialtyDropdown(true)
                                            if (e.target.value.trim()) {
                                                setShowAddHint(true)
                                            }
                                        }}
                                        onFocus={() =>
                                            setShowSpecialtyDropdown(true)
                                        }
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault()
                                                handleAddSpecialty()
                                                setShowAddHint(false)
                                            } else if (e.key === 'Escape') {
                                                setShowSpecialtyDropdown(false)
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddSpecialty}
                                        className="btn btn-secondary"
                                        disabled={!specialtyInput.trim()}
                                    >
                                        Adicionar
                                    </button>
                                </div>

                                {showSpecialtyDropdown &&
                                    (hasSuggestions ||
                                        specialtyInput.trim()) && (
                                        <div className="absolute z-50 w-full mt-2 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                            {/* Especialidades cadastradas no servidor */}
                                            {filteredServerSpecialties.length >
                                                0 && (
                                                <>
                                                    <div className="px-3 py-2 text-xs font-semibold text-base-content/50 bg-base-200/50 border-b border-base-300">
                                                        Cadastradas
                                                    </div>
                                                    {filteredServerSpecialties.map(
                                                        specialty => (
                                                            <button
                                                                key={
                                                                    specialty.id
                                                                }
                                                                type="button"
                                                                className="w-full text-left p-3 hover:bg-base-200 border-b border-base-300 last:border-b-0"
                                                                onClick={() => {
                                                                    setSpecialtyInput(
                                                                        specialty.name
                                                                    )
                                                                    setShowSpecialtyDropdown(
                                                                        false
                                                                    )
                                                                    setShowAddHint(
                                                                        true
                                                                    )
                                                                }}
                                                            >
                                                                <div className="font-medium">
                                                                    {
                                                                        specialty.name
                                                                    }
                                                                </div>
                                                            </button>
                                                        )
                                                    )}
                                                </>
                                            )}

                                            {/* Sugestoes da lista comum */}
                                            {commonSuggestions.length > 0 && (
                                                <>
                                                    <div className="px-3 py-2 text-xs font-semibold text-base-content/50 bg-base-200/50 border-b border-base-300">
                                                        Sugestões
                                                    </div>
                                                    {commonSuggestions.map(
                                                        (name, index) => (
                                                            <button
                                                                key={`suggestion-${index}`}
                                                                type="button"
                                                                className="w-full text-left p-3 hover:bg-base-200 border-b border-base-300 last:border-b-0"
                                                                onClick={() => {
                                                                    setSpecialtyInput(
                                                                        name
                                                                    )
                                                                    setShowSpecialtyDropdown(
                                                                        false
                                                                    )
                                                                    setShowAddHint(
                                                                        true
                                                                    )
                                                                }}
                                                            >
                                                                <div className="font-medium text-base-content/80">
                                                                    {name}
                                                                </div>
                                                            </button>
                                                        )
                                                    )}
                                                </>
                                            )}

                                            {/* Mensagem quando não há resultados */}
                                            {!hasSuggestions &&
                                                specialtyInput.trim() && (
                                                    <div className="p-3 text-sm text-base-content/60">
                                                        Pressione Enter ou
                                                        clique em "Adicionar"
                                                        para criar uma nova
                                                        especialidade
                                                    </div>
                                                )}
                                        </div>
                                    )}

                                {/* Dica para clicar em Adicionar */}
                                {showAddHint && specialtyInput.trim() && (
                                    <div className="mt-2 flex items-center justify-between bg-info/10 border border-info/30 rounded-lg px-3 py-2">
                                        <span className="text-sm text-info">
                                            Clique em "Adicionar" ou pressione
                                            Enter para incluir a especialidade
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setShowAddHint(false)
                                            }
                                            className="btn btn-ghost btn-xs btn-circle"
                                        >
                                            x
                                        </button>
                                    </div>
                                )}
                            </div>

                            {selectedSpecialties.length > 0 && (
                                <div className="flex flex-col gap-3">
                                    {selectedSpecialties.map(
                                        (specialty, index) => (
                                            <div
                                                key={index}
                                                className="flex flex-wrap items-center gap-3 p-3 bg-base-200/50 rounded-lg border border-base-300/30"
                                            >
                                                <div className="flex items-center gap-2 min-w-[200px]">
                                                    <span className="font-medium text-sm">
                                                        {specialty.name}
                                                    </span>
                                                    {specialty.isNew && (
                                                        <span className="badge badge-sm badge-secondary !text-white">
                                                            Nova
                                                        </span>
                                                    )}
                                                </div>
                                                {/*<div className="form-control">
                                                    <label className="label py-0">
                                                        <span className="label-text text-xs">
                                                            Valor da Consulta (R$)
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm w-36"
                                                        placeholder="R$ 0,00"
                                                        value={specialty.consultationPrice || ''}
                                                        onChange={e => {
                                                            const updated = [...selectedSpecialties]
                                                            updated[index] = {
                                                                ...updated[index],
                                                                consultationPrice: currencyFormat(e.target.value),
                                                            }
                                                            setSelectedSpecialties(updated)
                                                        }}
                                                    />
                                                </div>
                                                <div className="form-control">
                                                    <label className="label py-0">
                                                        <span className="label-text text-xs">
                                                            Desconto Máximo (%)
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        max="100"
                                                        className="input input-bordered input-sm w-28"
                                                        placeholder="0"
                                                        value={specialty.maxDiscount || ''}
                                                        onChange={e => {
                                                            const updated = [...selectedSpecialties]
                                                            updated[index] = {
                                                                ...updated[index],
                                                                maxDiscount: e.target.value,
                                                            }
                                                            setSelectedSpecialties(updated)
                                                        }}
                                                    />
                                                </div>*/}
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleRemoveSpecialty(
                                                            index
                                                        )
                                                    }
                                                    className="btn btn-xs btn-circle btn-ghost text-error"
                                                >
                                                    x
                                                </button>
                                            </div>
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Subespecialidades */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Subespecialidades
                        </h2>
                        <div className="relative subspecialty-autocomplete-container">
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Buscar subespecialidade..."
                                value={subspecialtyInput}
                                onChange={e => {
                                    setSubspecialtyInput(e.target.value)
                                    setShowSubspecialtyDropdown(true)
                                }}
                                onFocus={() =>
                                    setShowSubspecialtyDropdown(true)
                                }
                                onKeyDown={e => {
                                    if (e.key === 'Escape')
                                        setShowSubspecialtyDropdown(false)
                                }}
                            />
                            {showSubspecialtyDropdown &&
                                subspecialtyInput.trim() &&
                                (() => {
                                    const filtered =
                                        allSpecialtiesWithSubs.filter(
                                            s =>
                                                !!s.parentId &&
                                                !selectedSubspecialties.some(
                                                    sel => sel.id === s.id
                                                ) &&
                                                !selectedSpecialties.some(
                                                    sel => sel.id === s.id
                                                ) &&
                                                s.name
                                                    .toLowerCase()
                                                    .includes(
                                                        subspecialtyInput
                                                            .toLowerCase()
                                                            .trim()
                                                    )
                                        )
                                    return filtered.length > 0 ? (
                                        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                            {filtered.slice(0, 15).map(sub => (
                                                <button
                                                    key={sub.id}
                                                    type="button"
                                                    className="w-full text-left px-4 py-2 hover:bg-base-200 border-b border-base-300/30 last:border-b-0"
                                                    onClick={() => {
                                                        setSelectedSubspecialties(
                                                            prev => [
                                                                ...prev,
                                                                {
                                                                    id: sub.id,
                                                                    name: sub.name,
                                                                    isNew: false,
                                                                },
                                                            ]
                                                        )
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            specialtyIds: [
                                                                ...prev.specialtyIds,
                                                                sub.id,
                                                            ],
                                                        }))
                                                        setSubspecialtyInput('')
                                                        setShowSubspecialtyDropdown(
                                                            false
                                                        )
                                                    }}
                                                >
                                                    <span className="text-sm font-medium">
                                                        {sub.name}
                                                    </span>
                                                    {sub.parent && (
                                                        <span className="text-xs text-base-content/50 ml-2">
                                                            ({sub.parent.name})
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl p-3">
                                            <p className="text-sm text-base-content/60">
                                                Nenhuma subespecialidade
                                                encontrada
                                            </p>
                                        </div>
                                    )
                                })()}
                        </div>
                        {selectedSubspecialties.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                                {selectedSubspecialties.map((sub, index) => {
                                    const fullSub = allSpecialtiesWithSubs.find(
                                        s => s.id === sub.id
                                    )
                                    return (
                                        <span
                                            key={sub.id || index}
                                            className="badge badge-secondary gap-1 py-3"
                                        >
                                            {sub.name}
                                            {fullSub?.parent && (
                                                <span className="opacity-70 text-xs">
                                                    ({fullSub.parent.name})
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedSubspecialties(
                                                        prev =>
                                                            prev.filter(
                                                                s =>
                                                                    s.id !==
                                                                    sub.id
                                                            )
                                                    )
                                                    if (sub.id)
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            specialtyIds:
                                                                prev.specialtyIds.filter(
                                                                    id =>
                                                                        id !==
                                                                        sub.id
                                                                ),
                                                        }))
                                                }}
                                            >
                                                <MdClose className="text-sm" />
                                            </button>
                                        </span>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {!hidePartnerSelector && (
                    <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                        <div className="card-body">
                            <h2 className="card-title text-lg mb-4">
                                Parceiro Vinculado (opcional)
                            </h2>
                            <PartnerSelector
                                value={formData.partnerId}
                                onChange={(partnerId: number | null) =>
                                    setFormData(prev => ({
                                        ...prev,
                                        partnerId,
                                    }))
                                }
                                label=""
                                placeholder="Selecione um parceiro (opcional)"
                            />
                        </div>
                    </div>
                )}

                {/* Hospital é entidade da saúde: fora dela, quem vincula o
                    profissional ao local de atendimento é "Estabelecimentos
                    Vinculados", logo abaixo. */}
                {isHealth && (
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-1">
                            Hospitais Vinculados
                        </h2>
                        <p className="text-sm text-base-content/60 mb-4">
                            Busque pelo nome. Antes esta seção listava todos os
                            hospitais cadastrados como caixas de seleção, e com muitos
                            deles o resto do formulário ficava longe demais para rolar.
                        </p>
                        <HospitalSearchSelector
                            multi
                            label=""
                            placeholder="Buscar hospital por nome..."
                            value={formData.hospitalIds}
                            onChange={ids =>
                                setFormData(prev => ({
                                    ...prev,
                                    hospitalIds: ids,
                                }))
                            }
                            disabled={loading}
                            emptyText="Nenhum hospital cadastrado. Cadastre em Cadastros Gerais para vincular ao profissional."
                        />
                    </div>
                </div>
                )}

                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Estabelecimentos Vinculados
                        </h2>
                        <div className="relative establishment-autocomplete-container">
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Buscar estabelecimento..."
                                value={establishmentSearch}
                                onChange={e => {
                                    setEstablishmentSearch(e.target.value)
                                    setShowEstablishmentDropdown(true)
                                }}
                                onFocus={() =>
                                    setShowEstablishmentDropdown(true)
                                }
                                onKeyDown={e => {
                                    if (e.key === 'Escape')
                                        setShowEstablishmentDropdown(false)
                                }}
                            />
                            {showEstablishmentDropdown &&
                                establishmentSearch.trim() &&
                                (() => {
                                    const filtered = establishments.filter(
                                        e =>
                                            !formData.establishmentIds.includes(
                                                e.id
                                            ) &&
                                            (e.name
                                                .toLowerCase()
                                                .includes(
                                                    establishmentSearch
                                                        .toLowerCase()
                                                        .trim()
                                                ) ||
                                                e.tradingName
                                                    ?.toLowerCase()
                                                    .includes(
                                                        establishmentSearch
                                                            .toLowerCase()
                                                            .trim()
                                                    ))
                                    )
                                    return filtered.length > 0 ? (
                                        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                            {filtered.slice(0, 15).map(est => (
                                                <button
                                                    key={est.id}
                                                    type="button"
                                                    className="w-full text-left px-4 py-2 hover:bg-base-200 border-b border-base-300/30 last:border-b-0"
                                                    onClick={() => {
                                                        handleChange(
                                                            'establishmentIds',
                                                            [
                                                                ...formData.establishmentIds,
                                                                est.id,
                                                            ]
                                                        )
                                                        setEstablishmentSearch(
                                                            ''
                                                        )
                                                        setShowEstablishmentDropdown(
                                                            false
                                                        )
                                                    }}
                                                >
                                                    <span className="text-sm font-medium">
                                                        {est.tradingName ||
                                                            est.name}
                                                    </span>
                                                    {est.tradingName && (
                                                        <span className="text-xs text-base-content/50 ml-2">
                                                            ({est.name})
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl p-3">
                                            <p className="text-sm text-base-content/60">
                                                Nenhum estabelecimento
                                                encontrado
                                            </p>
                                        </div>
                                    )
                                })()}
                        </div>
                        {formData.establishmentIds.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                                {formData.establishmentIds.map(eid => {
                                    const est = establishments.find(
                                        e => e.id === eid
                                    )
                                    return (
                                        <span
                                            key={eid}
                                            className="badge badge-secondary gap-1 py-3"
                                        >
                                            {est?.tradingName ||
                                                est?.name ||
                                                `#${eid}`}
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleChange(
                                                        'establishmentIds',
                                                        formData.establishmentIds.filter(
                                                            id => id !== eid
                                                        )
                                                    )
                                                }
                                            >
                                                <MdClose className="text-sm" />
                                            </button>
                                        </span>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-1">Endereços</h2>
                        <p className="text-sm text-base-content/60 mb-4">
                            Um profissional pode atender em mais de um local. Dê
                            um título a cada endereço (ex.: &quot;Consultório
                            Centro&quot;) para que o operador saiba qual escolher
                            no agendamento.
                        </p>
                        <AddressManager
                            value={addresses}
                            onChange={setAddresses}
                            disabled={loading}
                            emptyHint="Nenhum endereço cadastrado para este profissional."
                        />
                    </div>
                </div>

                <PayoutScheduleSection
                    value={payoutSchedule}
                    onChange={setPayoutSchedule}
                    description="Defina como os valores serão repassados a este profissional."
                    transfer={{
                        value: payoutTransfer,
                        onChange: setPayoutTransfer,
                    }}
                />

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/person/professionals')}
                        className="btn btn-ghost"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                Salvar
                            </>
                        )}
                    </button>
                </div>
            </form>
            {photoCrop.cropDialog}
        </div>
    )
}
